import { ColDef } from "@ag-grid-community/core";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from "@chakra-ui/react";
import { AxiosError } from "axios";
import { useRefresh } from "features/fees/dashboard-pages/FeeBasisTab/useRefresh";
import { BasisAdjustmentsTableRow } from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisAdjustmentsTable";
import { useFeeChargeEventPointInTimeTransactionCodesListSuspense } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import { usePortfolioTransactionListSuspense } from "gen/endpoints/portfolio-transactions/portfolio-transactions";
import { get } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { MaybernTableDateCellRenderer } from "shared/components/table/maybern/MaybernTableDateCellRenderer";
import { useBulkEditor } from "shared/components/table/maybern/useBulkEditor";
import { useToast } from "shared/components/toast/useToast";
import { ModalCancelButton } from "shared/modal/ModalCancelButton";
import { flattenApiErrorData, getApiError } from "shared/utils/ErrorUtils";

export interface BulkPortTx {
  fundFamilyId: string;
  onClose: () => void;
  isOpen: boolean;
  feeChargeEventSelected: string;
}

export const BulkAddPortTx: React.FC<BulkPortTx> = ({
  fundFamilyId,
  onClose,
  isOpen,
  feeChargeEventSelected,
}) => {
  const { t } = useTranslation(["commitment", "common"]);
  const { showErrorToast } = useToast();
  const {
    isUpsertPending: isSubmitting,
    onSubmitDraft,
    upsertError,
    upsertVariables,
  } = useRefresh({ fundFamilyId, feeChargeEventSelected, showToasts: false });

  const { data: portfolioTransactionListData } =
    usePortfolioTransactionListSuspense(fundFamilyId, {
      include_only_drafts: true,
    });

  const { data: transactionCodeList } =
    useFeeChargeEventPointInTimeTransactionCodesListSuspense(fundFamilyId);

  const { data: investmentListData } = useInvestmentListSuspense(fundFamilyId, {
    is_allocable: true,
  });

  const columnDefs = useMemo(
    (): ColDef<BasisAdjustmentsTableRow>[] => [
      {
        field: "transaction_code_id",
        headerName: t(
          "fees:feeUseBreakdown.basisChangeTable.transactionCode.columnTitle",
        ),
        width: 300,
        type: ["dropdownEditable"],
        cellEditorParams: {
          title: t(
            "fees:feeUseBreakdown.basisChangeTable.transactionCode.inputTitle",
          ),
          options: transactionCodeList.map(({ id, name }) => ({
            value: id,
            label: name,
            children: name,
          })),
        },
      },
      {
        field: "investment_id",
        headerName: t("fees:feeUseBreakdown.basisChangeTable.investment.title"),
        width: 260,
        type: ["dropdownEditable"],
        cellEditorParams: {
          title: t("fees:feeUseBreakdown.basisChangeTable.investment.title"),
          options: investmentListData.map(({ id, legal_name }) => ({
            value: id,
            label: legal_name,
            children: legal_name,
          })),
        },
      },
      {
        field: "amount",
        headerName: t("common:amount"),
        type: ["moneyEditable"],
        editable: true,
        initialWidth: 200,
      },
      {
        field: "effective_date",
        headerName: t("common:effectiveDate"),
        width: 180,
        cellRenderer: MaybernTableDateCellRenderer,
      },
    ],
    [investmentListData, t, transactionCodeList],
  );

  const { onClickClear, rowData, tableProps } =
    useBulkEditor<BasisAdjustmentsTableRow>({
      columnDefs,
      controllerProps: {
        initialRowData: portfolioTransactionListData,
        getSubmittedFieldValue: useCallback(
          (rowIndex, field) =>
            get(
              upsertVariables,
              `data.portfolio_transactions.${rowIndex}.${field}`,
            ),
          [upsertVariables],
        ),
        getFieldError: useCallback(
          (rowIndex, field) => {
            const errData = (
              upsertError as
                | AxiosError<{ error: { data: Record<string, string> } }>
                | undefined
            )?.response?.data.error.data;
            const path = `portfolio_transactions.${rowIndex}.${field}`;
            const err = get(errData, path);
            return err;
          },
          [upsertError],
        ),
      },
    });

  const onSubmit = useCallback(async () => {
    try {
      await onSubmitDraft(rowData.filter((v) => !!v));
      onClose();
    } catch (e) {
      const errorData = getApiError(e);
      const rootListError =
        flattenApiErrorData(errorData?.data).find(
          (error) => error.fieldPath === "portfolio_transactions",
        )?.error || errorData?.message;

      if (rootListError) {
        showErrorToast(rootListError);
      }
    }
  }, [onClose, onSubmitDraft, rowData, showErrorToast]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalBody px="0" display="flex" flexDirection="column">
          <TableBox isBulkEditor={true}>
            <TableBoxHeader bg="eggplant.25">
              <TableBoxTitle>
                {t("fees:feeUseBreakdown.bulkAdd.title")}
              </TableBoxTitle>
              <TableBoxActions showFullScreen={false}>
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={onClickClear}
                >
                  {t("common:clear")}
                </Button>
              </TableBoxActions>
            </TableBoxHeader>
            <TableBoxBody display="flex" minHeight="0">
              <MaybernTable {...tableProps} />
            </TableBoxBody>
          </TableBox>
        </ModalBody>
        <ModalFooter borderTopWidth="1px" gap="8px" py="12px" px="24px">
          <ModalCancelButton />
          <Button
            onClick={onSubmit}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {t("common:refresh")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
